import TinyColor from "@ctrl/tinycolor";
import { useLoad } from "@redotech/react-util/load";
import { BrandKit } from "@redotech/redo-model/brand-kit";
import {
  CustomFieldType,
  InputElement,
} from "@redotech/redo-model/content-builder/element-schema";
import { BrandKitOverrides } from "@redotech/redo-model/signup-form-schema";
import { assertNever } from "@redotech/util/type";
import classNames from "classnames";
import { memo, NamedExoticComponent, useContext, useState } from "react";
import { supportedCountryCodes } from "../../arbiter-components/input/phone-number-country";
import { BrandKitDateInput } from "../../brand-kit-date-input";
import { BrandKitDropdownInput } from "../../brand-kit-dropdown-input";
import { BrandKitRadioGroup } from "../../brand-kit-radio-group";
import {
  BrandKitPhoneNumberInput,
  BrandKitTextAreaInput,
  BrandKitTextInput,
} from "../../brand-kit-text-input";
import { Flex } from "../../flex";
import { ExternalLink } from "../../link";
import { Text } from "../../text";
import * as signupFormCss from "../signup-form.module.css";
import { getBaseElementStyles } from "./base-element-styles";
import { CustomFieldContext } from "./context";
import { FormElementProps } from "./index";

const EmailInput = memo(function EmailInput({
  brandKitOverrides,
  brandKit,
  element,
  isBuilder,
}: FormElementProps<InputElement>) {
  const [value, setValue] = useState("");
  return (
    <BrandKitTextInput
      brandKit={brandKit}
      inputTypeHint="email"
      name="email"
      override={
        brandKitOverrides?.inputs.enabled
          ? brandKitOverrides?.inputs
          : undefined
      }
      placeholder={element.placeholderText}
      required={!element.optional}
      setValue={setValue}
      state={isBuilder ? "disabled" : undefined}
      value={value}
    />
  );
});

const PhoneInput = memo(function PhoneInput({
  brandKitOverrides,
  brandKit,
  backgroundColor,
  element,
  isBuilder,
}: FormElementProps<InputElement>) {
  const [value, setValue] = useState("");
  return (
    <>
      <BrandKitPhoneNumberInput
        brandKit={brandKit}
        name="phone"
        override={
          brandKitOverrides?.inputs.enabled
            ? brandKitOverrides?.inputs
            : undefined
        }
        required={!element.optional}
        setValue={setValue}
        state={isBuilder ? "disabled" : undefined}
        supportedCountryCodes={supportedCountryCodes}
        value={value}
      />
      <Text
        className={classNames(
          signupFormCss.legalDisclaimer,
          TinyColor(backgroundColor).isDark()
            ? signupFormCss.invertedTextColor
            : undefined,
        )}
        dir="column"
        mt="lg"
      >
        By providing your phone number, you agree to receive recurring automated
        marketing text messages (e.g. cart reminders) from this shop. Consent is
        not a condition to obtain goods or services. Msg & data rates may apply.
        Msg frequency varies. Reply HELP for help and STOP to cancel. View{" "}
        <ExternalLink
          showIcon={false}
          url="https://www.getredo.com/terms-conditions"
        >
          Terms of Service
        </ExternalLink>{" "}
        and{" "}
        <ExternalLink
          showIcon={false}
          url="https://www.getredo.com/privacy-policy"
        >
          Privacy Policy.
        </ExternalLink>
      </Text>
    </>
  );
});

const BirthdayInput = memo(function BirthdayInput({
  brandKitOverrides,
  brandKit,
  element,
  isBuilder,
}: FormElementProps<InputElement>) {
  const [value, setValue] = useState<Temporal.PlainDate | null>(null);
  return (
    <BrandKitDateInput
      brandKit={brandKit}
      name="birthday"
      override={
        brandKitOverrides?.inputs.enabled
          ? brandKitOverrides?.inputs
          : undefined
      }
      required={!element.optional}
      setValue={setValue}
      state={isBuilder ? "disabled" : undefined}
      value={value}
    />
  );
});

const CustomDateField = memo(function CustomDateField({
  brandKitOverrides,
  brandKit,
  element,
  isBuilder,
}: {
  brandKitOverrides?: BrandKitOverrides;
  element: InputElement;
  brandKit: BrandKit;
  isBuilder?: boolean;
}) {
  const [value, setValue] = useState<Temporal.PlainDate | null>(null);
  return (
    <BrandKitDateInput
      brandKit={brandKit}
      name={element.customField?.id}
      override={
        brandKitOverrides?.inputs.enabled
          ? brandKitOverrides?.inputs
          : undefined
      }
      required={!element.optional}
      setValue={setValue}
      state={isBuilder ? "disabled" : undefined}
      value={value}
    />
  );
});

const CustomDropdownField = memo(function CustomDropdownField({
  brandKitOverrides,
  element,
  brandKit,
  isBuilder,
}: {
  brandKitOverrides?: BrandKitOverrides;
  element: InputElement;
  brandKit: BrandKit;
  isBuilder?: boolean;
}) {
  const getOptionsForField = useContext(CustomFieldContext);
  const [value, setValue] = useState<string | null>(null);

  const fieldOptions = useLoad(async () => {
    if (element.customField?.id && getOptionsForField) {
      const result = await getOptionsForField(element.customField.id);
      return result.options;
    }
    return undefined;
  }, [element.customField?.id, getOptionsForField]);

  return (
    <BrandKitDropdownInput
      brandKit={brandKit}
      disabled={isBuilder}
      name={element.customField?.id}
      options={fieldOptions.value ?? []}
      override={
        brandKitOverrides?.inputs.enabled
          ? brandKitOverrides?.inputs
          : undefined
      }
      placeholder={element.placeholderText}
      required={!element.optional}
      setValue={(value) => setValue(value)}
      value={value}
    />
  );
});

const CustomNumberField = memo(function CustomNumberField({
  brandKitOverrides,
  brandKit,
  element,
  isBuilder,
}: {
  brandKitOverrides?: BrandKitOverrides;
  element: InputElement;
  brandKit: BrandKit;
  isBuilder?: boolean;
}) {
  const [value, setValue] = useState<string>("");
  return (
    <BrandKitTextInput
      brandKit={brandKit}
      htmlPattern="^[0-9]*$"
      name={element.customField?.id}
      override={
        brandKitOverrides?.inputs.enabled
          ? brandKitOverrides?.inputs
          : undefined
      }
      placeholder={element.placeholderText}
      required={!element.optional}
      setValue={(newValue) => {
        if (newValue === "" || /^[0-9]*$/.test(newValue)) {
          setValue(newValue);
        }
      }}
      state={isBuilder ? "disabled" : undefined}
      value={value}
    />
  );
});

const CustomLongTextField = memo(function CustomLongTextField({
  brandKitOverrides,
  element,
  brandKit,
  isBuilder,
}: {
  brandKitOverrides?: BrandKitOverrides;
  element: InputElement;
  brandKit: BrandKit;
  isBuilder?: boolean;
}) {
  const [value, setValue] = useState<string>("");
  return (
    <BrandKitTextAreaInput
      brandKit={brandKit}
      disabled={isBuilder}
      override={
        brandKitOverrides?.inputs.enabled
          ? brandKitOverrides?.inputs
          : undefined
      }
      placeholder={element.placeholderText}
      required={!element.optional}
      setValue={setValue}
      value={value}
    />
  );
});

const CustomTextShortField = memo(function CustomTextShortField({
  brandKitOverrides,
  brandKit,
  element,
  isBuilder,
}: {
  brandKitOverrides?: BrandKitOverrides;
  element: InputElement;
  brandKit: BrandKit;
  isBuilder?: boolean;
}) {
  const [value, setValue] = useState<string>("");
  return (
    <BrandKitTextInput
      brandKit={brandKit}
      name={element.customField?.id}
      override={
        brandKitOverrides?.inputs.enabled
          ? brandKitOverrides?.inputs
          : undefined
      }
      placeholder={element.placeholderText}
      required={!element.optional}
      setValue={setValue}
      state={isBuilder ? "disabled" : undefined}
      value={value}
    />
  );
});

const CustomYesNoField = memo(function CustomYesNoField({
  brandKitOverrides,
  brandKit,
  element,
  isBuilder,
}: {
  brandKitOverrides?: BrandKitOverrides;
  element: InputElement;
  brandKit: BrandKit;
  isBuilder?: boolean;
}) {
  const [value, setValue] = useState<boolean>(true);
  return (
    <BrandKitRadioGroup
      brandKit={brandKit}
      disabled={isBuilder}
      name={element.customField?.id}
      options={[
        { id: true, label: "Yes" },
        { id: false, label: "No" },
      ]}
      override={
        brandKitOverrides?.inputs.enabled
          ? brandKitOverrides?.inputs
          : undefined
      }
      required={!element.optional}
      setValue={(value) => typeof value === "boolean" && setValue(value)}
      value={value}
    />
  );
});

const CustomInput = memo(function CustomInput({
  brandKitOverrides,
  brandKit,
  element,
  isBuilder,
}: FormElementProps<InputElement>) {
  if (!element.customField?.fieldType) {
    return null;
  }

  switch (element.customField.fieldType) {
    case CustomFieldType.Date:
      return (
        <CustomDateField
          brandKit={brandKit}
          brandKitOverrides={brandKitOverrides}
          element={element}
          isBuilder={isBuilder}
        />
      );
    case CustomFieldType.Dropdown:
      return (
        <CustomDropdownField
          brandKit={brandKit}
          brandKitOverrides={brandKitOverrides}
          element={element}
          isBuilder={isBuilder}
        />
      );
    case CustomFieldType.Number:
      return (
        <CustomNumberField
          brandKit={brandKit}
          brandKitOverrides={brandKitOverrides}
          element={element}
          isBuilder={isBuilder}
        />
      );
    case CustomFieldType.TextLong:
      return (
        <CustomLongTextField
          brandKit={brandKit}
          brandKitOverrides={brandKitOverrides}
          element={element}
          isBuilder={isBuilder}
        />
      );
    case CustomFieldType.TextShort:
      return (
        <CustomTextShortField
          brandKit={brandKit}
          brandKitOverrides={brandKitOverrides}
          element={element}
          isBuilder={isBuilder}
        />
      );
    case CustomFieldType.YesNoQuestion:
      return (
        <CustomYesNoField
          brandKit={brandKit}
          brandKitOverrides={brandKitOverrides}
          element={element}
          isBuilder={isBuilder}
        />
      );
    default:
      assertNever(element.customField.fieldType);
  }
});

const componentMap: Record<
  InputElement["inputType"],
  NamedExoticComponent<FormElementProps<InputElement>>
> = {
  email: EmailInput,
  phone: PhoneInput,
  birthday: BirthdayInput,
  custom: CustomInput,
};

export const Input = memo(function Input(
  props: FormElementProps<InputElement>,
) {
  const { element } = props;
  const Component = componentMap[element.inputType];

  if (!Component) {
    return null;
  }

  const styles = getBaseElementStyles(element);
  const hasStyles = Object.keys(styles).length > 0;

  if (hasStyles) {
    return (
      <Flex dir="column" style={styles} w="full">
        <Component {...props} />
      </Flex>
    );
  }

  return <Component {...props} />;
});
